/**
 * AccountSatisfactionSection (#63) — klanttevredenheid van dit account uit de
 * evaluaties (enquête-dashboard). Toont het gemiddelde cijfer + aantal, de laatste
 * paar evaluaties, en linkt door naar alle evaluaties voorgefilterd op deze klant.
 */
import React, { useEffect, useState } from 'react';
import { Paper, Typography, Stack, Box, CircularProgress, Chip, Link } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { Link as RouterLink } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { useUserRole } from '../../contexts/UserRoleContext';

const scoreColor = (v) => {
  if (v == null) return '#9ca3af';
  if (v >= 4.5) return '#10b981';
  if (v >= 4.0) return '#f59e0b';
  if (v >= 3.5) return '#f97316';
  return '#ef4444';
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: '2-digit' }) : '');

export default function AccountSatisfactionSection({ accountName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // De samenvatting is voor iedereen; het volledige dashboard alleen met het Klanttevredenheid-recht.
  const { permissions } = useUserRole();
  const magDashboard = !!permissions?.enquete_dashboard?.view;

  useEffect(() => {
    if (!accountName) { setLoading(false); return; }
    authFetch(`${BACKEND_URL}/Api/enquete/by-client?name=${encodeURIComponent(accountName)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountName]);

  if (loading) return <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}><CircularProgress size={20} /></Paper>;
  if (!data || !data.count) return null; // geen evaluaties → sectie verbergen

  const link = `/enquete-dashboard?klant=${encodeURIComponent(accountName)}`;

  return (
    <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>⭐ Klanttevredenheid</Typography>
        {magDashboard && (
          <Link component={RouterLink} to={link} sx={{ fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
            Alle evaluaties →
          </Link>
        )}
      </Stack>

      <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <StarIcon sx={{ color: scoreColor(data.gemiddelde), fontSize: 30 }} />
            <Typography variant="h4" fontWeight={800} sx={{ color: scoreColor(data.gemiddelde) }}>
              {data.gemiddelde != null ? data.gemiddelde.toFixed(1) : '–'}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">gemiddeld cijfer</Typography>
        </Box>
        <Chip label={`${data.count} evaluatie${data.count === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
      </Stack>

      <Stack spacing={0.75}>
        {(data.laatste || []).map((e) => (
          <Stack key={e.id} direction="row" spacing={1.5} alignItems="center"
            sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #eef2f6' }}>
            <Chip size="small" label={e.gemiddelde != null ? Number(e.gemiddelde).toFixed(1) : '–'}
              sx={{ fontWeight: 800, bgcolor: scoreColor(e.gemiddelde), color: '#fff', minWidth: 40 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{e.projectnaam || 'Project onbekend'}</Typography>
              {e.opmerking ? (
                <Typography variant="caption" color="text.secondary" sx={{
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>"{e.opmerking}"</Typography>
              ) : null}
            </Box>
            <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>{fmtDate(e.datum)}</Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
